import * as React from "react";
import {
  type CustomCell,
  type CustomRenderer,
  getMiddleCenterBias,
  GridCellKind,
  TextCellEntry,
} from "@glideapps/glide-data-grid";

interface ImageTextCellProps {
  readonly kind: "image-text-cell";
  readonly image?: string;
  readonly name?: string;
}

export type ImageTextCell = CustomCell<ImageTextCellProps>;

export const ImageTextCellRenderer: CustomRenderer<ImageTextCell> = {
  kind: GridCellKind.Custom,
  isMatch: (cell: CustomCell): cell is ImageTextCell => (cell.data as any).kind === "image-text-cell",
  draw: (args, cell) => {
    const {ctx, rect, theme, imageLoader, col, row} = args;
    const {image, name} = cell.data;

    const xPad = theme.cellHorizontalPadding;

    const radius = Math.min(12, rect.height / 2 - theme.cellVerticalPadding);

    const drawX = rect.x + xPad;

    ctx.save();

    let imageSet = false

    if (image) {
      const imageResult = imageLoader.loadOrGetImage(image, col, row);
      if (imageResult !== undefined) {
        ctx.save();
        ctx.beginPath();
        ctx.arc(drawX + radius, rect.y + rect.height / 2, radius, 0, Math.PI * 2);
        ctx.clip();

        ctx.drawImage(imageResult, drawX, rect.y + rect.height / 2 - radius, radius * 2, radius * 2);

        ctx.restore();

        imageSet = true
      }
    }

    if (name !== undefined) {
      // @ts-ignore
      ctx.font = theme.baseFontFull;
      ctx.fillStyle = theme.textDark;

      const x = imageSet ? drawX + radius * 2 + xPad : drawX
      ctx.fillText(name, x, rect.y + rect.height / 2 + getMiddleCenterBias(ctx, theme));
    }

    ctx.restore();

    return true;
  },
  provideEditor: () => p => {
    const {isHighlighted, onChange, value} = p;
    return (
      <TextCellEntry
        highlight={isHighlighted}
        autoFocus={true}
        value={value.data.name ?? ""}
        disabled={value.readonly ?? false}
        onChange={e =>
          onChange({
            ...value,
            data: {
              ...value.data,
              name: e.target.value,
            },
          })
        }
      />
    );
  },
  onPaste: (v, d) => ({
    ...d,
    name: v,
  }),
};
